<template>
  <div class="text-center" data-testid="verify-email">
    <Notification
      :message="notification"
      :trigger-notification="showNotification"
      class="w-screen fixed left-0 top-15 z-50"
      @toggle-visibility="showNotification = false"
    />

    <EmailVerificationIllustration class="mx-auto" aria-hidden="true" />

    <component :is="headingTag" :id="headingId" :class="headingClass">{{ headline }}</component>

    <p class="text-body mt-24">
      Check your inbox for a verification link. We sent it to
      <span class="font-medium" data-testid="verify-email-address">{{ displayedEmail }}</span
      >.
    </p>

    <p class="text-body mt-16">
      Verifying your email confirms it's really you, helps keep your account secure, and reduces fake or harmful activity on
      Sittercity.
    </p>

    <p v-if="showPendingJobApplicationNotice" data-testid="pending-job-application-notice" class="text-body mt-16">
      Your job application is on hold until you verify your email. Once verified, we'll send it automatically.
    </p>

    <p v-if="showPendingMessageDraftNotice" data-testid="pending-message-draft-notice" class="text-body mt-16">
      Your message is on hold until you verify your email. Once verified, we'll return you to send it.
    </p>

    <div
      v-if="!isVerified"
      data-testid="verify-email-waiting"
      class="flex flex-col items-center gap-12 mt-32"
      role="status"
      aria-live="polite"
    >
      <Spinner :width="32" :height="32" aria-hidden="true" />
      <p class="text-body">Waiting for verification…</p>
    </div>

    <p v-else data-testid="verify-email-verified-label" class="text-subtitle text-success-contrast mt-32">
      Your email is verified
    </p>

    <div v-if="!isVerified" class="mt-32 flex justify-center">
      <Button
        value="Resend email"
        size="medium"
        class="w-full sm:w-auto"
        :loading="resending"
        :disabled="resending"
        @click="resendVerificationEmail"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import Notification from '@/components/notification/Notification.vue';
import Button from '@/components/button/Button.vue';
import Spinner from '@/components/spinner/Spinner.vue';
import EmailVerificationIllustration from '@/graphics/illustrations/EmailVerificationIllustration.vue';
import { useVerifyEmailWaiting } from '@/apps/verifyEmail/useVerifyEmailWaiting';

defineOptions({ name: 'VerifyEmailWaiting' });

const props = withDefaults(
  defineProps<{
    headline?: string;
    headingTag?: string;
    headingClass?: string;
    headingId?: string;
    accountEmail?: string;
    showPendingNotices?: boolean;
    active?: boolean;
  }>(),
  {
    headline: 'Verify your email',
    headingTag: 'h1',
    headingClass: 'text-h3 mt-24 md:mt-32',
    headingId: undefined,
    accountEmail: '',
    showPendingNotices: false,
    active: true,
  }
);

const emit = defineEmits<{
  verified: [];
}>();

const {
  displayedEmail,
  showPendingJobApplicationNotice,
  showPendingMessageDraftNotice,
  notification,
  showNotification,
  resending,
  resendVerificationEmail,
  isVerified,
} = useVerifyEmailWaiting(props, emit);
</script>
